"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const ReturnAssetContext = createContext();

export const ReturnAssetProvider = ({ children }) => {
  const [assignedAssets, setAssignedAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Fetch Assigned Assets
  const fetchAssignedAssets = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/assets/assigned`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch assigned assets");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setAssignedAssets(data.data || []);
      } else {
        setError(data.message || "Failed to fetch assigned assets");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching assigned assets");
    } finally {
      setLoading(false);
    }
  };

  // Return Asset
  const returnAsset = async (payload) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/assets/return`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to return asset",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchAssignedAssets();

        toast.success(data.message || "Asset returned successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to return asset",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error returning asset",
      };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = getToken();

    if (token) {
      fetchAssignedAssets();
    }
  }, []);

  return (
    <ReturnAssetContext.Provider
      value={{
        assignedAssets,
        loading,
        error,
        fetchAssignedAssets,
        returnAsset,
      }}
    >
      {children}
    </ReturnAssetContext.Provider>
  );
};

export const useReturnAssets = () => useContext(ReturnAssetContext);